// Pixel mode conversion utilities for Image > Mode pipeline.
// Each function operates on a PixelBuffer in-place and returns metadata
// about the conversion (e.g. palette for indexed, bit depth changes).

import type { PixelBuffer } from "./PixelBuffer";

// ---------------------------------------------------------------------------
// Grayscale — RGB → luminance (0.299R + 0.587G + 0.114B)
// ---------------------------------------------------------------------------

export function rgbToGrayscale(buffer: PixelBuffer): void {
  const data = buffer.data;
  for (let i = 0; i < data.length; i += 4) {
    const lum = Math.round(0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!);
    data[i] = lum;
    data[i + 1] = lum;
    data[i + 2] = lum;
  }
  buffer.markDirty();
}

// ---------------------------------------------------------------------------
// Indexed Color — reduce to 256-color palette using median-cut
// ---------------------------------------------------------------------------

export interface IndexedResult {
  palette: [number, number, number][];
  indices: Uint8Array;
}

function medianCut(pixels: [number, number, number][], maxDepth: number): [number, number, number][] {
  if (pixels.length === 0) return [[0, 0, 0]];
  if (maxDepth <= 0 || pixels.length <= 1) {
    const avg = averageColor(pixels);
    return [avg];
  }

  let rMin = 255, rMax = 0, gMin = 255, gMax = 0, bMin = 255, bMax = 0;
  for (const [r, g, b] of pixels) {
    if (r < rMin) rMin = r; if (r > rMax) rMax = r;
    if (g < gMin) gMin = g; if (g > gMax) gMax = g;
    if (b < bMin) bMin = b; if (b > bMax) bMax = b;
  }

  const rRange = rMax - rMin;
  const gRange = gMax - gMin;
  const bRange = bMax - bMin;
  const maxRange = Math.max(rRange, gRange, bRange);

  let sortAxis: 0 | 1 | 2 = 0;
  if (maxRange === gRange) sortAxis = 1;
  else if (maxRange === bRange) sortAxis = 2;

  pixels.sort((a, b) => a[sortAxis]! - b[sortAxis]!);

  const mid = Math.floor(pixels.length / 2);
  const left = pixels.slice(0, mid);
  const right = pixels.slice(mid);

  return [...medianCut(left, maxDepth - 1), ...medianCut(right, maxDepth - 1)];
}

function averageColor(pixels: [number, number, number][]): [number, number, number] {
  if (pixels.length === 0) return [0, 0, 0];
  let r = 0, g = 0, b = 0;
  for (const [pr, pg, pb] of pixels) {
    r += pr; g += pg; b += pb;
  }
  const n = pixels.length;
  return [Math.round(r / n), Math.round(g / n), Math.round(b / n)];
}

export function rgbToIndexed(buffer: PixelBuffer, maxColors = 256): IndexedResult {
  const data = buffer.data;
  const w = buffer.width;
  const h = buffer.height;
  const pixelCount = w * h;

  // Collect unique colors (sample if too many for performance)
  const colorSet = new Map<string, [number, number, number]>();
  const sampleStep = pixelCount > 10000 ? Math.ceil(pixelCount / 10000) : 1;

  for (let i = 0; i < pixelCount; i += sampleStep) {
    const idx = i * 4;
    const key = `${data[idx]!},${data[idx + 1]!},${data[idx + 2]!}`;
    if (!colorSet.has(key)) {
      colorSet.set(key, [data[idx]!, data[idx + 1]!, data[idx + 2]!]);
    }
  }

  const uniqueColors = Array.from(colorSet.values());

  // Build palette using median-cut
  let palette: [number, number, number][];
  if (uniqueColors.length <= maxColors) {
    palette = uniqueColors;
  } else {
    palette = medianCut(uniqueColors, Math.ceil(Math.log2(maxColors)));
    if (palette.length > maxColors) {
      palette = palette.slice(0, maxColors);
    }
  }

  // Map each pixel to nearest palette color
  const indices = new Uint8Array(pixelCount);
  for (let i = 0; i < pixelCount; i++) {
    const idx = i * 4;
    const r = data[idx]!, g = data[idx + 1]!, b = data[idx + 2]!;
    let bestDist = Infinity;
    let bestIdx = 0;
    for (let p = 0; p < palette.length; p++) {
      const [pr, pg, pb] = palette[p]!;
      const dr = r - pr, dg = g - pg, db = b - pb;
      const dist = dr * dr + dg * dg + db * db;
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = p;
        if (dist === 0) break;
      }
    }
    indices[i] = bestIdx;
    // Replace pixel data with palette color
    data[idx] = palette[bestIdx]![0]!;
    data[idx + 1] = palette[bestIdx]![1]!;
    data[idx + 2] = palette[bestIdx]![2]!;
  }

  buffer.markDirty();
  return { palette, indices };
}

// ---------------------------------------------------------------------------
// Lab Color — RGB → XYZ (D65, sRGB's native white point) → Bradford-adapt →
// CIE Lab against the D50 illuminant (Photoshop's Lab working space / the
// ICC profile connection space convention).
// ---------------------------------------------------------------------------

// D50 reference white — Lab values in this module (and in ColorPicker.ts's
// Lab tab) are computed relative to D50, matching Photoshop's Image > Mode >
// Lab Color. A naive swap of the white-point constants without also
// chromatically adapting the XYZ (computed via the D65-relative sRGB matrix)
// would shift hues, so rgbToLabPixel/labToRgbPixel Bradford-adapt between
// D65 and D50 around the XYZ<->Lab step (#i221).
const Xn = 96.422;
const Yn = 100.0;
const Zn = 82.521;

// Bradford chromatic-adaptation matrices (Lindbloom), applied to XYZ values
// scaled 0-100.
const BRADFORD_D65_TO_D50: readonly (readonly number[])[] = [
  [1.0478112, 0.0228866, -0.0501270],
  [0.0295424, 0.9904844, -0.0170491],
  [-0.0092345, 0.0150436, 0.7521316],
];
const BRADFORD_D50_TO_D65: readonly (readonly number[])[] = [
  [0.9555766, -0.0230393, 0.0631636],
  [-0.0282895, 1.0099416, 0.0210077],
  [0.0122982, -0.0204830, 1.3299098],
];

function applyMatrix3(
  m: readonly (readonly number[])[],
  x: number,
  y: number,
  z: number,
): [number, number, number] {
  return [
    m[0]![0]! * x + m[0]![1]! * y + m[0]![2]! * z,
    m[1]![0]! * x + m[1]![1]! * y + m[1]![2]! * z,
    m[2]![0]! * x + m[2]![1]! * y + m[2]![2]! * z,
  ];
}

function fLab(t: number): number {
  const delta = 6 / 29;
  return t > delta * delta * delta
    ? Math.cbrt(t)
    : t / (3 * delta * delta) + 4 / 29;
}

function fLabInv(t: number): number {
  const delta = 6 / 29;
  return t > delta
    ? t * t * t
    : 3 * delta * delta * (t - 4 / 29);
}

export interface LabColor {
  L: number;
  a: number;
  b: number;
}

export function rgbToLabPixel(r: number, g: number, b: number): LabColor {
  // sRGB → linear
  const linR = r <= 10 ? r / 3294.6 : Math.pow((r / 266.386) + 0.0513, 2.4);
  const linG = g <= 10 ? g / 3294.6 : Math.pow((g / 266.386) + 0.0513, 2.4);
  const linB = b <= 10 ? b / 3294.6 : Math.pow((b / 266.386) + 0.0513, 2.4);

  // Linear RGB → XYZ (sRGB matrix, relative to its native D65 white point)
  const x65 = (linR * 0.4124564 + linG * 0.3575761 + linB * 0.1804375) * 100;
  const y65 = (linR * 0.2126729 + linG * 0.7151522 + linB * 0.0721750) * 100;
  const z65 = (linR * 0.0193339 + linG * 0.1191920 + linB * 0.9503041) * 100;

  // Bradford-adapt D65 → D50 before computing Lab against the D50 white point.
  const [x, y, z] = applyMatrix3(BRADFORD_D65_TO_D50, x65, y65, z65);

  // XYZ → Lab
  const fx = fLab(x / Xn);
  const fy = fLab(y / Yn);
  const fz = fLab(z / Zn);

  return {
    L: Math.round((116 * fy - 16) * 100) / 100,
    a: Math.round(500 * (fx - fy) * 100) / 100,
    b: Math.round(200 * (fy - fz) * 100) / 100,
  };
}

export function labToRgbPixel(L: number, a: number, b: number): [number, number, number] {
  // Lab → XYZ (D50)
  const fy = (L + 16) / 116;
  const fx = a / 500 + fy;
  const fz = fy - b / 200;

  const x50 = Xn * fLabInv(fx);
  const y50 = Yn * fLabInv(fy);
  const z50 = Zn * fLabInv(fz);

  // Bradford-adapt D50 → D65 to match the sRGB matrix's native white point.
  const [x, y, z] = applyMatrix3(BRADFORD_D50_TO_D65, x50, y50, z50);

  // XYZ → linear RGB
  const linR = x * 0.032404542 - y * 0.015371385 - z * 0.004985314;
  const linG = -x * 0.009692660 + y * 0.018760108 + z * 0.000415560;
  const linB = x * 0.000155436 - y * 0.002040259 + z * 0.010572252;

  // Linear RGB → sRGB
  const toSRgb = (c: number): number => {
    const clamped = Math.max(0, Math.min(1, c));
    return clamped <= 0.0031308
      ? Math.round(clamped * 12.92 * 255)
      : Math.round((1.055 * Math.pow(clamped, 1 / 2.4) - 0.055) * 255);
  };

  return [toSRgb(linR), toSRgb(linG), toSRgb(linB)];
}

export function rgbToLab(buffer: PixelBuffer): LabColor[] {
  const data = buffer.data;
  const labs: LabColor[] = [];
  for (let i = 0; i < data.length; i += 4) {
    const lab = rgbToLabPixel(data[i]!, data[i + 1]!, data[i + 2]!);
    labs.push(lab);
    // Reconstruct RGB via the near-lossless Lab → RGB round trip so the
    // image keeps its visual appearance in Lab mode (Photoshop parity)
    // instead of collapsing to an L-only grayscale preview (#i132).
    const [r, g, b] = labToRgbPixel(lab.L, lab.a, lab.b);
    data[i] = r;
    data[i + 1] = g;
    data[i + 2] = b;
  }
  buffer.markDirty();
  return labs;
}

// ---------------------------------------------------------------------------
// CMYK Color — RGB → CMYK
// ---------------------------------------------------------------------------

export interface CmykColor {
  c: number;
  m: number;
  y: number;
  k: number;
}

export function rgbToCmykPixel(r: number, g: number, b: number): CmykColor {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const k = 1 - Math.max(rn, gn, bn);

  if (k === 1) {
    return { c: 0, m: 0, y: 0, k: 100 };
  }

  return {
    c: Math.round((1 - rn - k) / (1 - k) * 10000) / 100,
    m: Math.round((1 - gn - k) / (1 - k) * 10000) / 100,
    y: Math.round((1 - bn - k) / (1 - k) * 10000) / 100,
    k: Math.round(k * 10000) / 100,
  };
}

export function cmykToRgbPixel(c: number, m: number, y: number, k: number): [number, number, number] {
  const cn = c / 100;
  const mn = m / 100;
  const yn = y / 100;
  const kn = k / 100;

  if (kn >= 1) {
    return [0, 0, 0];
  }

  return [
    Math.round(255 * (1 - cn) * (1 - kn)),
    Math.round(255 * (1 - mn) * (1 - kn)),
    Math.round(255 * (1 - yn) * (1 - kn)),
  ];
}

export function rgbToCmyk(buffer: PixelBuffer): CmykColor[] {
  const data = buffer.data;
  const cmyks: CmykColor[] = [];
  for (let i = 0; i < data.length; i += 4) {
    const cmyk = rgbToCmykPixel(data[i]!, data[i + 1]!, data[i + 2]!);
    cmyks.push(cmyk);
    // Store CMYK preview: convert back to RGB for display
    const [r, g, b] = cmykToRgbPixel(cmyk.c, cmyk.m, cmyk.y, cmyk.k);
    data[i] = r;
    data[i + 1] = g;
    data[i + 2] = b;
  }
  buffer.markDirty();
  return cmyks;
}

// ---------------------------------------------------------------------------
// 16 Bits/Channel — expand 8-bit to 16-bit
// ---------------------------------------------------------------------------

export function expand8to16(buffer: PixelBuffer): Uint16Array {
  const data8 = buffer.data;
  const data16 = new Uint16Array(data8.length);
  for (let i = 0; i < data8.length; i++) {
    // Map 8-bit [0..255] to 16-bit [0..65535] preserving full range
    data16[i] = (data8[i]! << 8) | data8[i]!;
  }
  return data16;
}

// ---------------------------------------------------------------------------
// 8 Bits/Channel — quantize 16-bit to 8-bit
// ---------------------------------------------------------------------------

export function quantize16to8(data16: Uint16Array): Uint8ClampedArray {
  const data8 = new Uint8ClampedArray(data16.length);
  for (let i = 0; i < data16.length; i++) {
    // Map 16-bit [0..65535] to 8-bit [0..255]
    data8[i] = data16[i]! >> 8;
  }
  return data8;
}
